import axios from "axios";
import eventsOddsHandler from "@/services/eventsOddsHandlerV3";
import { loadPinnedMarkets } from '@/services/pinnedMarketsStorage';
import assign from 'lodash/assign';

const DEFAULT_MARKETS_LIMIT = 200;

// --- LOCAL LOOKUP TABLES ---
let eventsDetailLookup = {};
let oddsDetailLookup = {};

//stable version 3.0 fixed memory leak 16/04/2026


function mapDetailOddsToLookup(market) {
    if (!market || !market.outcome || !Array.isArray(market.outcome)) return;
    for (let k = 0; k < market.outcome.length; k++) {
        const odd = market.outcome[k];
        if (odd && odd.unique_id) {
            oddsDetailLookup[odd.unique_id] = {odd: odd, market: market};
        }
    }
}

const state = {
    eventsDetail: [],
    eventsDetailActive: [],
    loading: false,
    loadingMore: false,
    pagination: null,
    marketsGroups: [],
    availableMarkets: []
};

const getters = {
    getEventsDetail: (state) => state.eventsDetail,
    getEventsDetailActive: (state) => state.eventsDetailActive,
    getLoadingDetail: (state) => state.loading,
    isLoadingMoreDetail: (state) => state.loadingMore,
    getPaginationDetail: (state) => state.pagination,
    getMarketsGroupsDetailPrematch: (state) => state.marketsGroups,
    getAvailableMarkets: (state) => state.availableMarkets
};

const mutations = {
    SET_LOADING(state, status) {
        state.loading = status;
    },

    SET_LOADING_MORE(state, status) {
        state.loadingMore = status;
    },

    SET_PAGINATION(state, data) {
        state.pagination = data;
    },

    SET_AVAILABLE_MARKETS(state, list) {
        state.availableMarkets = list;
    },

    SET_EVENT_DETAIL(state, payload) {
        state.eventsDetail = payload;

        eventsDetailLookup = {};
        oddsDetailLookup = {};

        if (payload && Array.isArray(payload)) {
            payload.forEach(obj => {
                if (obj.event && obj.event.id) {
                    eventsDetailLookup[obj.event.id] = obj.event;

                    if (obj.event.odds && Array.isArray(obj.event.odds)) {
                        obj.event.odds.forEach(marketGroup => {
                            if (Array.isArray(marketGroup)) {
                                marketGroup.forEach(m => mapDetailOddsToLookup(m));
                            } else {
                                mapDetailOddsToLookup(marketGroup);
                            }
                        });
                    }
                }
            });
        }
    },

    ADD_EVENT_DETAIL(state, payload) {
        state.eventsDetail.unshift(payload);
    },

    APPEND_MARKETS_EVENT_DETAIL(state, payload) {
        const targetId = payload.source || payload.event.id;
        const index = state.eventsDetail.findIndex(e => e.source == targetId || (e.event && e.event.id == targetId));

        if (index !== -1) {
            const existingEvent = state.eventsDetail[index];
            const incomingOdds = payload.event.odds || [];

            incomingOdds.forEach(newMarketGroup => {
                existingEvent.event.odds.push(newMarketGroup);

                if (Array.isArray(newMarketGroup)) {
                    newMarketGroup.forEach(m => mapDetailOddsToLookup(m));
                } else {
                    mapDetailOddsToLookup(newMarketGroup);
                }
            });

            state.eventsDetail.splice(index, 1, existingEvent);
        }
    },

    UPDATE_ODDS_EVENT_DETAIL(state, payload) {
        const targetId = payload.source || payload.event.id;

        const index = state.eventsDetail.findIndex(e =>
            e.source == targetId || (e.event && e.event.id == targetId)
        );

        if (index !== -1) {
            const updatedDetail = {
                ...state.eventsDetail[index],
                event: {
                    ...state.eventsDetail[index].event,
                    odds: payload.event.odds
                }
            };

            if (payload.event.odds && Array.isArray(payload.event.odds)) {
                payload.event.odds.forEach(marketGroup => {
                    if (Array.isArray(marketGroup)) {
                        marketGroup.forEach(m => mapDetailOddsToLookup(m));
                    } else {
                        mapDetailOddsToLookup(marketGroup);
                    }
                });
            }

            state.eventsDetail.splice(index, 1, updatedDetail);
        } else {
            console.warn("UPDATE_ODDS_EVENT_DETAIL: Evento non trovato in memoria", targetId);
        }
    },

    REMOVE_FROM_EVENT_DETAIL(state, source) {
        try {
            // Find the event before removing it
            const eventToRemove = state.eventsDetail.find(e => e.source == source || (e.event && e.event.id == source));

            // Clean up ONLY its odds from the lookup table
            if (eventToRemove && eventToRemove.event && Array.isArray(eventToRemove.event.odds)) {
                eventToRemove.event.odds.forEach(marketGroup => {
                    const markets = Array.isArray(marketGroup) ? marketGroup : [marketGroup];
                    markets.forEach(m => {
                        if (m && Array.isArray(m.outcome)) {
                            m.outcome.forEach(odd => {
                                if (odd && odd.unique_id) delete oddsDetailLookup[odd.unique_id];
                            });
                        }
                    });
                });
            }
        } catch (e) {
            console.error("Safe cleanup failed in Prematch REMOVE_FROM_EVENT_DETAIL:", e);
        }

        // Remove the event
        state.eventsDetail = state.eventsDetail.filter(event => event.source !== source && (!event.event || event.event.id !== source));
        delete eventsDetailLookup[source];

        // Fallback cleanup just in case
        if (state.eventsDetail.length === 0) {
            oddsDetailLookup = {};
        }
        state.pagination = null;
    },

    UPDATE_LIVESCORE_FROM_SOCKET(state, payload) {
        const updates = Array.isArray(payload) ? payload : [payload];
        for (let i = 0; i < updates.length; i++) {
            const change = updates[i];
            const event = eventsDetailLookup[change.id];

            if (event && change.livescore) {
                const incomingData = Array.isArray(change.livescore) ? change.livescore[0] : change.livescore;
                if (!incomingData) continue;

                if (!event.livescore) {
                    event.livescore = incomingData;
                } else {
                    if (incomingData.scoreboard) {
                        if (!event.livescore.scoreboard) event.livescore.scoreboard = {};
                        assign(event.livescore.scoreboard, incomingData.scoreboard);
                    }
                    if (incomingData.periods) event.livescore.periods = incomingData.periods;
                    if (incomingData.statistics) event.livescore.statistics = incomingData.statistics;
                    if (incomingData.matchStatus) event.livescore.matchStatus = incomingData.matchStatus;
                }
            }
        }
    },

    UPDATE_ODDS_FROM_SOCKET(state, payload) {
        if (!payload || !Array.isArray(payload)) return;

        for (let i = 0; i < payload.length; i++) {
            const eventPayload = payload[i];
            if (eventPayload.odds && eventPayload.odds.set) {
                const oddChanges = eventPayload.odds.set;

                for (let j = 0; j < oddChanges.length; j++) {
                    const changeItem = oddChanges[j];
                    const uniqueId = changeItem.unique_id;
                    const attr = changeItem._attributes;
                    const lookupRef = oddsDetailLookup[uniqueId];

                    if (lookupRef && attr) {
                        const targetOdd = lookupRef.odd;
                        const targetMarket = lookupRef.market;
                        const newValue = parseFloat(attr.odds);
                        const oldValue = parseFloat(targetOdd.outcome_odd_value);

                        if (newValue > oldValue) targetOdd.type_change = 'up';
                        else if (newValue < oldValue) targetOdd.type_change = 'down';

                        targetOdd.outcome_odd_value = newValue;
                        targetOdd.status = parseInt(attr.status);
                        // Freeze-suspend authoritative — task 13.
                        if (targetOdd.frozenApplied !== true) {
                            targetOdd.active = (targetOdd.status === 1);
                        }
                        // Boost invalidation on socket tick — the socket
                        // payload carries the RAW provider odd, so a
                        // previously-applied boost is no longer visible in
                        // `outcome_odd_value`. Drop the marker until a
                        // full-refresh from the server confirms the boost
                        // still applies (e.g. once the event flips to live
                        // and the resolver checks a different context, the
                        // prematch boost rule is skipped). Without this the
                        // player sees the raw value with a stale "+N%"
                        // chip stuck to it.
                        if (targetOdd.boostApplied === true) {
                            targetOdd.boostApplied = false;
                            targetOdd.boostPercent = null;
                            targetOdd.oddsPreBoost = null;
                        }

                        if (targetMarket && attr.line) targetMarket.specifier = attr.line;
                    }
                }
            }
        }
    },

    SET_EVENTS_DETAIL_ACTIVE(state, payload) {
        state.eventsDetailActive = payload;
    },
    ADD_EVENTS_DETAIL_ACTIVE(state, source) {
        if (!state.eventsDetailActive.includes(source)) state.eventsDetailActive.push(source);
    },
    REMOVE_EVENTS_DETAIL_ACTIVE(state, source) {
        state.eventsDetailActive = state.eventsDetailActive.filter(id => id !== source);
    },
    SET_MARKETS_GROUPS(state, payload) {
        state.marketsGroups = payload;
    }
};

const actions = {
    async setLoadingDetail(context, status) {
        context.commit('SET_LOADING', status);
    },

    async fetchEventDetailV3(context, payload) {
        const page = payload.page || 1;

        const limit = payload.limit
            || context.rootGetters['settings/getPrematchDetailMaxMarkets']
            || DEFAULT_MARKETS_LIMIT;

        if (page === 1 && !context.state.eventsDetailActive.includes(payload.source)) {
            if (!payload.skipHistory) {
                await context.dispatch('sportNavigation/pushHistory', null, { root: true });
            }
            context.commit('SET_EVENTS_DETAIL_ACTIVE', [payload.source]);
            context.commit('SET_LOADING', true);
        } else if (page > 1) {
            context.commit('SET_LOADING_MORE', true);
        }

        const requestPayload = {
            platformId: context.rootGetters['settings/getPlatformId'],
            sportId: payload.sport,
            categoryId: payload.category,
            tournamentId: payload.tournament,
            eventId: payload.source,
            groupId: payload.group === 0 ? null : payload.group,
            type: "groupOdds",
            section: "sport",
            typegroup: "detail",
            lang: context.rootGetters['settings/getLanguage'],
            product_id: 0,
            page: page,
            limit: limit
        };

        if (payload.sportName) requestPayload.sportName = payload.sportName;
        if (payload.categoryName) requestPayload.categoryName = payload.categoryName;
        if (payload.tournamentName) requestPayload.tournamentName = payload.tournamentName;
        if (payload.competitorHomeId) requestPayload.competitorHomeId = payload.competitorHomeId;
        if (payload.competitorAwayId) requestPayload.competitorAwayId = payload.competitorAwayId;
        if (payload.competitorHome) requestPayload.competitorHome = payload.competitorHome;
        if (payload.competitorAway) requestPayload.competitorAway = payload.competitorAway;

        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];

        try {
            const res = await axios.post(`${apiUrl}/v3/events/prematch/event-detail`, requestPayload, {headers: {Authorization: `Bearer ${token}`}});

            if (res.data.messages === "Success Operation" && res.data.event != null) {
                if (res.data.marketsGroups) context.commit('SET_MARKETS_GROUPS', res.data.marketsGroups);

                if (res.data.pagination) {
                    context.commit('SET_PAGINATION', res.data.pagination);
                }

                const data_event = await eventsOddsHandler.refactorEventDetailV3(res.data);

                if (page === 1) {
                    context.commit('SET_EVENT_DETAIL', [data_event]);

                    window.parent.postMessage({
                        type: 'eventOpened',
                        eventId: payload.source,
                        sportId: payload.sport,
                        categoryId: payload.category,
                        tournamentId: payload.tournament,
                        eventName: payload.competitorHome + ' ' + payload.competitorAway,
                        eventNameBack: payload.eventName || null
                    }, "*");
                } else {
                    context.commit('APPEND_MARKETS_EVENT_DETAIL', data_event);
                }
            } else {
                if (page === 1) {
                    context.commit('SET_EVENT_DETAIL', []);
                    context.commit('SET_MARKETS_GROUPS', []);
                    context.dispatch('showAlertEventNotAvailable', payload.source);
                } else if (res.data.pagination) {
                    context.commit('SET_PAGINATION', res.data.pagination);
                }
            }
        } catch (error) {
            console.error("Error fetchEventDetailV3:", error);
            if (page === 1) {
                context.commit('SET_EVENT_DETAIL', []);
                context.dispatch('showAlertEventNotAvailable', payload.source);
            }
        } finally {
            if (page === 1) context.commit('SET_LOADING', false);
            context.commit('SET_LOADING_MORE', false);
        }

        if (payload.featuredTournament == null || payload.featuredTournament === false) {
            context.dispatch('settings/setStatusBannerCentral', false, {root: true});
        }
    },

    async fetchEventDetailV3Outcomes(context, payload) {
        const requestPayload = {
            platformId: context.rootGetters['settings/getPlatformId'],
            sportId: payload.sport,
            categoryId: payload.category,
            tournamentId: payload.tournament,
            eventId: payload.source,
            groupId: payload.group === 0 ? null : payload.group,
            type: "groupOdds",
            section: "sport",
            typegroup: "detail",
            lang: context.rootGetters['settings/getLanguage'],
            product_id: 0,
            page: 1,
            limit: payload.limit || context.rootGetters['settings/getPrematchDetailMaxMarkets'] || DEFAULT_MARKETS_LIMIT
        };

        if (payload.marketId != null) {
            requestPayload.marketId = payload.marketId;
        }

        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];

        try {
            const res = await axios.post(`${apiUrl}/v3/events/prematch/event-detail`, requestPayload, {headers: {Authorization: `Bearer ${token}`}});

            if (res.data.messages === "Success Operation" && res.data.event != null) {
                const odds = res.data.event.odds || [];
                return odds.flat().flatMap(market => market.outcome || []);
            }
        } catch (error) {
            console.error("Error fetchEventDetailV3Outcomes:", error);
        }

        return [];
    },

    async fetchSingleAntepostV3(context, payload) {
        context.commit('SET_LOADING', true);

        if (payload.isSwitchRace) {
            if (payload.oldSource) {
                context.commit('previewPrematch/REMOVE_TOURNAMENT_ACTIVE', payload.oldSource + '#antepost', {root: true});
            }
        }

        context.commit('previewPrematch/ADD_TOURNAMENT_ACTIVE', payload.source + "#antepost", {root: true});

        const requestPayload = {
            eventId: payload.idEvent,
            sportId: payload.sportId,
            tournamentId: payload.tournamentId,
            categoryId: payload.categoryId,
            platformId: context.rootGetters['settings/getPlatformId'],
            language: context.rootGetters['settings/getLanguage']
        };
        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];

        try {
            const res = await axios.post(`${apiUrl}/v3/events/antepost/event-detail`, requestPayload, {headers: {Authorization: `Bearer ${token}`}});

            if (res.data.messages === "Success Operation" && res.data.event != null) {
                const data_event = await eventsOddsHandler.refactorEventAntepostDetailV3(res.data);
                context.commit('SET_EVENT_DETAIL', [data_event]);
            } else {
                context.commit('SET_EVENT_DETAIL', []);
                context.dispatch('showAlertEventNotAvailable', payload.source);
            }
        } catch (error) {
            console.error(error);
            context.commit('SET_EVENT_DETAIL', []);
            context.dispatch('showAlertEventNotAvailable', payload.source);
        } finally {
            context.commit('SET_LOADING', false);
        }
    },

    async fetchAntepost(context, payload) {
        await context.dispatch('fetchAntepostV3', payload);
    },

    async fetchAntepostV3(context, payload) {
        const activeTournaments = context.rootGetters['previewPrematch/getTournamentsActive'];
        if (!activeTournaments.includes(payload.source + "#antepost")) {
            context.commit('previewPrematch/ADD_TOURNAMENT_ACTIVE', payload.source + "#antepost", {root: true});
            const apiUrl = context.rootGetters['settings/getUrlApi'];
            const token = context.rootGetters['settings/getToken'];
            const requestPayload = {
                eventId: payload.idEvent,
                sportId: payload.sportId,
                tournamentId: payload.tournamentId,
                categoryId: payload.categoryId,
                platformId: context.rootGetters['settings/getPlatformId'],
                language: context.rootGetters['settings/getLanguage']
            };

            try {
                const res = await axios.post(`${apiUrl}/v3/events/antepost/event-detail`, requestPayload, {headers: {Authorization: `Bearer ${token}`}});

                if (res.data.messages === "Success Operation" && res.data.event != null) {
                    const data_event = await eventsOddsHandler.refactorEventAntepostDetailV3(res.data);
                    context.commit('ADD_EVENT_DETAIL', data_event);
                } else {
                    context.commit('previewPrematch/REMOVE_TOURNAMENT_ACTIVE', payload.source + '#antepost', { root: true });
                    context.dispatch('showAlertEventNotAvailable', payload.source);
                }
            } catch (error) {
                console.error("Error fetchAntepostV3:", error);
                context.commit('previewPrematch/REMOVE_TOURNAMENT_ACTIVE', payload.source + '#antepost', { root: true });
                context.dispatch('showAlertEventNotAvailable', payload.source);
            }
        }
    },

    async fetchOddsGroupDetailV3(context, payload) {

        const page = payload.page || 1;
        const limit = payload.limit
            || context.rootGetters['settings/getPrematchDetailMaxMarkets']
            || DEFAULT_MARKETS_LIMIT;

        if (page > 1) {
            context.commit('SET_LOADING_MORE', true);
        }

        const requestPayload = {
            platformId: context.rootGetters['settings/getPlatformId'],
            sportId: payload.sport,
            categoryId: payload.category,
            tournamentId: payload.tournament,
            eventId: payload.source,
            groupId: payload.group === 0 ? null : payload.group,
            type: "groupOdds",
            section: "sport",
            typegroup: "detail",
            lang: context.rootGetters['settings/getLanguage'],
            product_id: 0,
            page: page,
            limit: limit
        };
        if (payload.marketId != null) requestPayload.marketId = payload.marketId;

        const existingDetail = context.state.eventsDetail.find(d => d.source === payload.source);
        const ev = existingDetail && existingDetail.event;
        if (ev) {
            if (ev.sportName) requestPayload.sportName = ev.sportName;
            if (ev.categoryName) requestPayload.categoryName = ev.categoryName;
            if (ev.tournamentName) requestPayload.tournamentName = ev.tournamentName;
            if (ev.home_id) requestPayload.competitorHomeId = ev.home_id;
            if (ev.away_id) requestPayload.competitorAwayId = ev.away_id;
            if (ev.home) requestPayload.competitorHome = ev.home;
            if (ev.away) requestPayload.competitorAway = ev.away;
        }

        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];

        try {
            const res = await axios.post(`${apiUrl}/v3/events/prematch/event-detail`, requestPayload, {headers: {Authorization: `Bearer ${token}`}});

            if (res.data.messages === "Success Operation" && res.data.event != null) {
                if (res.data.marketsGroups) context.commit('SET_MARKETS_GROUPS', res.data.marketsGroups);

                if (res.data.pagination) {
                    context.commit('SET_PAGINATION', res.data.pagination);
                }

                const data_event = await eventsOddsHandler.refactorEventDetailV3(res.data);

                if (page === 1) {
                    context.commit('UPDATE_ODDS_EVENT_DETAIL', data_event);
                } else {
                    context.commit('APPEND_MARKETS_EVENT_DETAIL', data_event);
                }
            } else {
                if (res.data.pagination) {
                    context.commit('SET_PAGINATION', res.data.pagination);
                }
            }
        } catch (error) {
            console.error("Error fetchOddsGroupDetailV3:", error);
        } finally {
            context.commit('SET_LOADING_MORE', false);
        }
    },

    async fetchAvailableMarkets(context, payload) {
        const requestPayload = {
            platformId: context.rootGetters['settings/getPlatformId'],
            sportId: payload.sport,
            eventId: payload.source,
            lang: context.rootGetters['settings/getLanguage'],
            product_id: 0
        };
        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];
        try {
            const res = await axios.post(`${apiUrl}/v3/events/prematch/event-available-markets`, requestPayload, { headers: { Authorization: `Bearer ${token}` } });
            const list = Array.isArray(res.data) ? res.data : (res.data && Array.isArray(res.data.markets) ? res.data.markets : []);
            context.commit('SET_AVAILABLE_MARKETS', list);
        } catch (error) {
            console.error("Error fetchAvailableMarkets:", error);
        }
    },

    async clearAllDetail(context) {
        context.state.eventsDetail.forEach(d => {
            if (d.type === 'antepost' && d.source) {
                context.commit('previewPrematch/REMOVE_TOURNAMENT_ACTIVE', d.source + '#antepost', { root: true });
            }
        });
        context.commit('SET_EVENT_DETAIL', []);
        context.commit('SET_EVENTS_DETAIL_ACTIVE', []);
        context.commit('SET_MARKETS_GROUPS', []);
    },

    async removeFromDetail(context, payload) {
        context.commit('REMOVE_FROM_EVENT_DETAIL', payload.source);
        context.commit('REMOVE_EVENTS_DETAIL_ACTIVE', payload.source);
        context.commit('SET_MARKETS_GROUPS', []);
        if (payload.type == 'antepost') context.commit('previewPrematch/REMOVE_TOURNAMENT_ACTIVE', payload.source + '#antepost', {root: true});
    },

    async removeFromDetailAntepostV3(context, payload) {
        context.dispatch('removeFromDetail', payload);
    },

    async showAlertEventNotAvailable(context, source) {
        context.dispatch('settings/setSnackbar', true, {root: true});
        context.dispatch('settings/setSnackbarText', 'eventsNoLongerAvailable', {root: true});
        context.commit('REMOVE_EVENTS_DETAIL_ACTIVE', source);
        setTimeout(() => context.dispatch('settings/setSnackbar', false, {root: true}), 2000);
    },

    // Fetches only the markets pinned by the user (from localStorage) for the open event.
    // If no markets are pinned, clears event odds so the view shows the empty state.
    async fetchFavouriteMarketsDetail(context, payload) {
        const marketIds = loadPinnedMarkets();
        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token  = context.rootGetters['settings/getToken'];

        if (!marketIds.length) {
            context.commit('UPDATE_ODDS_EVENT_DETAIL', { source: payload.source, event: { odds: [] } });
            return;
        }

        const body = {
            platformId:   context.rootGetters['settings/getPlatformId'],
            eventId:      payload.source,
            sportId:      payload.sport,
            categoryId:   payload.category,
            tournamentId: payload.tournament,
            marketIds,
            lang:         context.rootGetters['settings/getLanguage'],
        };

        try {
            const res = await axios.post(
                `${apiUrl}/v3/events/prematch/event-favourite-markets`, body,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data.messages === 'Success Operation' && res.data.event) {
                const data_event = await eventsOddsHandler.refactorEventDetailV3(res.data);
                context.commit('UPDATE_ODDS_EVENT_DETAIL', data_event);
            } else {
                context.commit('UPDATE_ODDS_EVENT_DETAIL', { source: payload.source, event: { odds: [] } });
            }
        } catch (error) {
            console.error('Error fetchFavouriteMarketsDetail:', error);
        }
    },

    async fetchBetBuilderMarkets(context, payload) {
        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token  = context.rootGetters['settings/getToken'];

        const body = {
            eventId:      payload.source,
            sportId:      payload.sport,
            categoryId:   payload.category,
            tournamentId: payload.tournament,
            lang:         context.rootGetters['settings/getLanguage'],
            page:         payload.page || 1,
            limit:        payload.limit || 100,
        };

        try {
            const res = await axios.post(
                `${apiUrl}/v3/events/bet-builder-markets`, body,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data.messages === 'Success Operation' && res.data.event) {
                const data_event = await eventsOddsHandler.refactorEventDetailV3(res.data);
                context.commit('UPDATE_ODDS_EVENT_DETAIL', data_event);
            } else {
                context.commit('UPDATE_ODDS_EVENT_DETAIL', { source: payload.source, event: { odds: [] } });
            }
        } catch (error) {
            console.error('Error fetchBetBuilderMarkets:', error);
            context.commit('UPDATE_ODDS_EVENT_DETAIL', { source: payload.source, event: { odds: [] } });
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};